import React, { useState } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Button,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import PageHeader from './shared/PageHeader';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'react-chartjs-2';
import { useAuth } from '../contexts/AuthContext';
import ContainerVanTypeahead from './ContainerVanTypeahead';
import type { ContainerVan } from '../types/containerVan';
import {
  fetchContainerVanReportData,
  calculateReportStats,
  formatChartData,
  getChartConfig,
  type ChartDataPoint,
  type ReportStats
} from '../utils/containerVanReportUtils';

// Register Chart.js components and zoom plugin
ChartJS.register(...registerables, zoomPlugin);

const ContainerVanChecklistReport: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, selectedCompany } = useAuth();

  // Form state
  const [selectedVan, setSelectedVan] = useState<ContainerVan | null>(null);
  const [vanSearchQuery, setVanSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });
  const [toDate, setToDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });

  // Data state
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleBack = () => {
    navigate('/container-van-checklist');
  };

  const validateForm = (): boolean => {
    if (!selectedVan && !vanSearchQuery.trim()) {
      toast({
        title: 'Van Selection Required',
        description: 'Please select a container van',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!fromDate) {
      toast({
        title: 'From Date Required',
        description: 'Please select a from date',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!toDate) {
      toast({
        title: 'To Date Required',
        description: 'Please select a to date',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    const from = new Date(fromDate);
    const to = new Date(toDate);

    if (from > to) {
      toast({
        title: 'Invalid Date Range',
        description: 'From date must be before to date',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleSearch = async () => {
    if (!validateForm() || !selectedCompany) return;

    try {
      setIsLoading(true);
      setHasSearched(true);

      const from = new Date(fromDate);
      const to = new Date(toDate);
      // Set to end of day
      to.setHours(23, 59, 59, 999);

      const data = await fetchContainerVanReportData(
        selectedVan?.vanNumber || vanSearchQuery.trim(),
        from,
        to,
        selectedCompany.companyId
      );
      console.log(data)
      setChartData(data);
      setStats(calculateReportStats(data));

      if (data.length === 0) {
        toast({
          title: 'No Data Found',
          description: 'No checklist data found for the specified criteria',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Data Loaded',
          description: `Found ${data.length} inspection records`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast({
        title: 'Search Error',
        description: 'Failed to fetch report data',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetChart = () => {
    const chartInstance = ChartJS.getChart('report-chart');
    if (chartInstance) {
      chartInstance.resetZoom();
    }
  };

  if (!user || !selectedCompany) {
    return (
      <Container maxW="md" py={8}>
        <VStack spacing={4}>
          <Text>Please log in and select a company to continue.</Text>
          <Button onClick={() => navigate('/container-van-checklist')} colorScheme="cyan">
            Back to Container Van Checklist
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
      <PageHeader
        title="Checklist Analytics"
        subtitle="Analytics & Trends"
        icon="📊"
        onBack={handleBack}
        rightContent={
          chartData.length > 0 ? (
            <Badge
              colorScheme="purple"
              fontSize="md"
              px={3}
              py={1}
              borderRadius="full"
              fontWeight="bold"
            >
              Van #{selectedVan?.vanNumber || vanSearchQuery}
            </Badge>
          ) : undefined
        }
      />

      <Container maxW="6xl" py={8} px={4}>
        <VStack spacing={6} w="100%">
          {/* Search Form */}
          <Card
            w="100%"
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            boxShadow="0 8px 32px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="whiteAlpha.500"
          >
            <CardBody>
              <VStack spacing={4}>
                
                <VStack spacing={4} w="100%">
                  <FormControl isRequired position="relative">
                    <FormLabel>Container Van</FormLabel>
                    <ContainerVanTypeahead
                      value={vanSearchQuery}
                      onChange={(query) => {
                        setVanSearchQuery(query);
                        // If user clears the search, clear the selected van
                        if (!query.trim()) {
                          setSelectedVan(null);
                        }
                      }}
                      onSelect={(van) => {
                        setSelectedVan(van);
                        setVanSearchQuery(van.vanNumber);
                      }}
                      placeholder="Search and select container van..."
                      companyId={selectedCompany?.companyId}
                      size="lg"
                    />
                  </FormControl>

                  {/* Selected Van Display */}
                  {selectedVan && (
                    <Card w="100%" bg="purple.50" borderColor="purple.200" variant="outline">
                      <CardBody py={3}>
                        <VStack spacing={2} align="start">
                          <HStack>
                            <CheckIcon color="purple.500" boxSize={4} />
                            <Text fontWeight="medium" fontSize="sm">Van Selected</Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.600">
                            Van: {selectedVan.vanNumber} | GenSet: {selectedVan.genSetNumber}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            Location: {selectedVan.location}
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  )}

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                    <FormControl isRequired>
                      <FormLabel>From Date</FormLabel>
                      <Input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        size="lg"
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>To Date</FormLabel>
                      <Input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        size="lg"
                      />
                    </FormControl>
                  </SimpleGrid>

                  <Button
                    onClick={handleSearch}
                    colorScheme="purple"
                    size="lg"
                    w="100%"
                    isLoading={isLoading}
                    loadingText="Searching..."
                  >
                    Generate Analytics
                  </Button>
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          {/* No Data Message */}
          {hasSearched && chartData.length === 0 && !isLoading && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text fontWeight="medium">No data found</Text>
                <Text fontSize="sm">
                  Try adjusting your search criteria or check if there are completed inspections for this van.
                </Text>
              </VStack>
            </Alert>
          )}

          {/* Statistics Cards */}
          {stats && chartData.length > 0 && (
            <Card w="100%" bg="white" boxShadow="md">
              <CardBody>
                <VStack spacing={4}>
                  <Text fontSize="lg" fontWeight="bold" color="purple.600" textAlign="center">
                    Statistics Overview
                  </Text>
                  
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="100%">
                    <Stat textAlign="center" p={4} bg="blue.50" borderRadius="md">
                      <StatLabel color="blue.600">Temperature (°C)</StatLabel>
                      <StatNumber color="blue.700">
                        {stats.temperature.highest}° / {stats.temperature.lowest}°
                      </StatNumber>
                      <StatHelpText color="blue.600">Highest / Lowest</StatHelpText>
                    </Stat>

                    <Stat textAlign="center" p={4} bg="red.50" borderRadius="md">
                      <StatLabel color="red.600">Fuel (%)</StatLabel>
                      <StatNumber color="red.700">
                        {stats.fuel.highest}% / {stats.fuel.lowest}%
                      </StatNumber>
                      <StatHelpText color="red.600">Highest / Lowest</StatHelpText>
                    </Stat>

                    <Stat textAlign="center" p={4} bg="green.50" borderRadius="md">
                      <StatLabel color="green.600">Remaining Hours</StatLabel>
                      <StatNumber color="green.700">
                        {stats.remaining_hours.highest.toFixed(1)}h / {stats.remaining_hours.lowest.toFixed(1)}h
                      </StatNumber>
                      <StatHelpText color="green.600">Highest / Lowest</StatHelpText>
                    </Stat>
                  </SimpleGrid>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Chart */}
          {chartData.length > 0 && (
            <Card w="100%" bg="white" boxShadow="md">
              <CardBody>
                <VStack spacing={4}>
                  <HStack justify="space-between" w="100%">
                    <Text fontSize="lg" fontWeight="bold" color="purple.600">
                      Trends Analysis
                    </Text>
                    <Button size="sm" onClick={resetChart} variant="outline">
                      Reset Zoom
                    </Button>
                  </HStack>
                  
                  <Divider />
                  
                  <Box w="100%" h={{ base: "400px", md: "500px" }}>
                    <Chart
                      id="report-chart"
                      type="line"
                      data={formatChartData(chartData)}
                      options={getChartConfig(selectedVan?.vanNumber || vanSearchQuery)}
                    />
                  </Box>
                  
                  <Box textAlign="center" mt={4}>
                    <Text fontSize="sm" color="gray.600">
                      📱 <strong>Mobile Tip:</strong> Pinch to zoom, drag to pan
                    </Text>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Loading State */}
          {isLoading && (
            <Card w="100%" bg="white" boxShadow="md">
              <CardBody>
                <VStack spacing={4} py={8}>
                  <Spinner size="lg" color="purple.500" />
                  <Text color="gray.600">Loading report data...</Text>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default ContainerVanChecklistReport;